import { useState, useMemo, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  Plus, Search, Wrench, Clock, AlertCircle, User as UserIcon,
  ChevronRight, Activity, CheckCircle2, DollarSign, Timer, Archive, ArchiveRestore, Shield, Bike,
} from 'lucide-react'
import { api, formatMoney } from '@/lib/api'
import { useFilial } from '@/lib/filial'
import { enterAbrePrimeiro } from '@/lib/keyboard'
import { pedirExclusao } from '@/lib/confirmarExclusao'

interface OS {
  id: number
  numero: number
  cliente_id: number
  cliente_nome?: string | null
  status: string
  prioridade: string
  tecnico_id: number | null
  data_abertura: string
  data_prevista: string | null
  valor_total: number | string
  descricao_problema: string | null
  eh_garantia?: boolean
  equipamento_nome?: string | null
}

const STATUS_META: Record<string, { label: string; cor: string; borda: string; ordem: number }> = {
  rascunho:               { label: 'Rascunho',          cor: 'bg-gray-100 text-gray-700',       borda: '#9ca3af', ordem: 0 },
  aguardando_diagnostico: { label: 'Diagnosticando',    cor: 'bg-amber-100 text-amber-800',     borda: '#d97706', ordem: 1 },
  aguardando_aprovacao:   { label: 'Aguard. aprovação', cor: 'bg-orange-100 text-orange-800',   borda: '#ea580c', ordem: 2 },
  aprovada:               { label: 'Aprovada',          cor: 'bg-blue-100 text-blue-800',       borda: '#2563eb', ordem: 3 },
  em_execucao:            { label: 'Em execução',       cor: 'bg-indigo-100 text-indigo-800',   borda: '#4f46e5', ordem: 4 },
  aguardando_peca:        { label: 'Aguard. peça',      cor: 'bg-yellow-100 text-yellow-800',   borda: '#ca8a04', ordem: 5 },
  concluida:              { label: 'Concluída',         cor: 'bg-teal-100 text-teal-800',       borda: '#0d9488', ordem: 6 },
  entregue:               { label: 'Entregue',          cor: 'bg-cyan-100 text-cyan-800',       borda: '#0891b2', ordem: 7 },
  faturada:               { label: 'Faturada',          cor: 'bg-emerald-100 text-emerald-800', borda: '#059669', ordem: 8 },
  encerrada:              { label: 'Encerrada',         cor: 'bg-emerald-50 text-emerald-700',  borda: '#10b981', ordem: 9 },
  em_garantia:            { label: 'Garantia',          cor: 'bg-rose-100 text-rose-800',       borda: '#e11d48', ordem: 10 },
  reprovada:              { label: 'Reprovada',         cor: 'bg-red-100 text-red-700',         borda: '#dc2626', ordem: 11 },
  cancelada:              { label: 'Cancelada',         cor: 'bg-red-100 text-red-700',         borda: '#dc2626', ordem: 12 },
}

const PRIO_META: Record<string, { label: string; cor: string }> = {
  baixa:    { label: 'Baixa',    cor: 'text-gray-500' },
  normal:   { label: 'Normal',   cor: 'text-gray-700' },
  alta:     { label: 'Alta',     cor: 'text-orange-600' },
  urgente:  { label: 'URGENTE',  cor: 'text-red-600 font-bold' },
}

function tempoDesde(iso: string | null): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  const min = Math.floor(ms / 60000)
  if (min < 1) return 'agora'
  if (min < 60) return `${min}m`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}d`
}

export default function OrdensServicoPage() {
  const nav = useNavigate()
  const [busca, setBusca] = useState('')
  const [filtroStatus, setFiltroStatus] = useState<string>('ativas')
  const [verArquivadas, setVerArquivadas] = useState(false)

  const filialAtualId = useFilial(s => s.filialAtual?.id ?? null)
  const modoConsolidado = useFilial(s => s.modoConsolidado)
  // Busca com atraso: evita uma consulta por tecla enquanto o operador digita.
  const [buscaServidor, setBuscaServidor] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setBuscaServidor(busca.trim()), 350)
    return () => clearTimeout(t)
  }, [busca])

  const { data: lista = [], isLoading, refetch } = useQuery<OS[]>({
    queryKey: ['ordens-servico', filialAtualId, modoConsolidado, verArquivadas, buscaServidor],
    queryFn: async () => (await api.get('/servicos', {
      // Buscando: vai ao banco (acha OS antiga, fora das 200 mais recentes) e
      // pega um limite maior. Sem busca, mantém o comportamento de sempre.
      params: {
        arquivadas: verArquivadas,
        ...(buscaServidor ? { q: buscaServidor, limit: 500 } : {}),
      },
    })).data,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    staleTime: 5000,
  })

  async function arquivarOS(e: React.MouseEvent, os: OS) {
    e.stopPropagation()
    try {
      await api.delete(`/servicos/${os.id}`)
      toast.success(`OS #${os.numero} arquivada`)
      refetch()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Erro ao arquivar')
    }
  }
  async function restaurarOS(e: React.MouseEvent, os: OS) {
    e.stopPropagation()
    try {
      await api.post(`/servicos/${os.id}/desarquivar`)
      toast.success(`OS #${os.numero} restaurada`)
      refetch()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Erro ao restaurar')
    }
  }

  const fechadas = new Set(['encerrada', 'reprovada', 'cancelada'])

  const kpis = useMemo(() => {
    const ativas = lista.filter(o => !fechadas.has(o.status))
    const emExec = lista.filter(o => o.status === 'em_execucao').length
    const aguardando = lista.filter(o => o.status === 'aguardando_aprovacao').length
    const emAberto = ativas.reduce((s, o) => s + Number(o.valor_total || 0), 0)
    return { ativas: ativas.length, emExec, aguardando, emAberto }
  }, [lista])

  const filtradas = useMemo(() => {
    let l = lista
    // Pesquisando, o operador quer achar a OS — inclusive encerrada/cancelada.
    // Sem busca, mantém o padrão de mostrar só as ativas.
    if (filtroStatus === 'ativas' && !verArquivadas && !busca.trim()) {
      l = l.filter(os => !fechadas.has(os.status))
    }
    else if (filtroStatus !== 'todas' && filtroStatus !== 'ativas') l = l.filter(os => os.status === filtroStatus)
    if (busca.trim()) {
      const q = busca.trim().toLowerCase()
      l = l.filter(os =>
        String(os.numero).includes(q) ||
        (os.cliente_nome || '').toLowerCase().includes(q) ||
        (os.descricao_problema || '').toLowerCase().includes(q),
      )
    }
    return [...l].sort((a, b) => new Date(b.data_abertura).getTime() - new Date(a.data_abertura).getTime())
  }, [lista, busca, filtroStatus, verArquivadas])

  // Chips dinâmicos: Ativas + Todas fixos, depois só os status que existem na
  // lista (com contagem), ordenados pelo fluxo. Evita filtro "fantasma" pra um
  // status sem nenhuma OS e garante que todo status presente tenha seu chip.
  const chips = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const o of lista) counts[o.status] = (counts[o.status] || 0) + 1
    const presentes = Object.keys(counts).sort(
      (a, b) => (STATUS_META[a]?.ordem ?? 99) - (STATUS_META[b]?.ordem ?? 99),
    )
    return [
      { key: 'ativas', label: 'Ativas', count: kpis.ativas },
      { key: 'todas', label: 'Todas', count: lista.length },
      ...presentes.map(s => ({ key: s, label: STATUS_META[s]?.label || s, count: counts[s] })),
    ]
  }, [lista, kpis.ativas])

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <span className="w-9 h-9 rounded-lg bg-violet-600 text-white flex items-center justify-center">
              <Wrench className="w-5 h-5" />
            </span>
            Ordens de Serviço
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Oficina, manutenção e serviços — acompanhe cada OS</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={() => nav('/servicos/novo')}>
          <Plus className="w-4 h-4" /> Nova OS
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <KpiCard icon={Activity} label="OS ativas" valor={kpis.ativas} cor="violet" />
        <KpiCard icon={Timer} label="Em execução" valor={kpis.emExec} cor="indigo" />
        <KpiCard icon={CheckCircle2} label="Aguard. aprovação" valor={kpis.aguardando} cor="orange" />
        <KpiCard icon={DollarSign} label="Em aberto" valor={formatMoney(kpis.emAberto)} cor="emerald" />
      </div>

      {/* Busca + chips */}
      <div className="flex flex-col gap-3 mb-5">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
          <input className="input pl-9" placeholder="Buscar por nº, cliente ou problema..."
            value={busca} onChange={e => setBusca(e.target.value)}
            onKeyDown={enterAbrePrimeiro(filtradas, os => nav(`/servicos/${os.id}`))} />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {chips.map(ch => {
            const ativo = filtroStatus === ch.key
            return (
              <button key={ch.key} onClick={() => setFiltroStatus(ch.key)}
                className={`text-xs px-3 py-1.5 rounded-full font-medium transition flex items-center gap-1.5 ${
                  ativo ? 'bg-violet-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>
                {ch.label}
                <span className={`text-[10px] rounded-full px-1.5 leading-4 ${ativo ? 'bg-white/25' : 'bg-gray-200 text-gray-500'}`}>{ch.count}</span>
              </button>
            )
          })}
          <button onClick={() => { setVerArquivadas(v => !v); setFiltroStatus('ativas') }}
            className={`ml-auto text-xs px-3 py-1.5 rounded-full font-medium transition flex items-center gap-1.5 border ${
              verArquivadas ? 'bg-amber-500 text-white shadow-sm border-amber-500' : 'bg-white text-gray-600 hover:bg-gray-100 border-gray-300'
            }`}>
            <Archive className="w-3.5 h-3.5" /> {verArquivadas ? 'Vendo arquivadas — voltar' : 'Arquivadas'}
          </button>
        </div>
      </div>

      {/* Grid de cards */}
      {isLoading ? (
        <div className="text-center py-16 text-gray-500">Carregando...</div>
      ) : filtradas.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-40" />
          {verArquivadas ? (
            <>
              <div className="mb-2 text-gray-500">Nenhuma OS arquivada.</div>
              <button onClick={() => { setVerArquivadas(false); setFiltroStatus('ativas'); setBusca('') }}
                className="text-violet-600 hover:underline text-sm">← Voltar para as ativas</button>
            </>
          ) : lista.length > 0 ? (
            <>
              <div className="mb-2 text-gray-500">Nenhuma OS com esse filtro.</div>
              <button onClick={() => { setFiltroStatus('todas'); setBusca('') }}
                className="text-violet-600 hover:underline text-sm">Ver todas as OS ({lista.length})</button>
            </>
          ) : (
            <>
              <div className="mb-1">Nenhuma OS encontrada</div>
              <button onClick={() => nav('/servicos/novo')} className="text-violet-600 hover:underline text-sm">
                + Abrir uma nova OS
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtradas.map(os => {
            const st = STATUS_META[os.status] || { label: os.status, cor: 'bg-gray-100', borda: '#9ca3af' }
            const pr = PRIO_META[os.prioridade] || PRIO_META.normal
            const urgente = os.prioridade === 'urgente'
            // FEATURE 06/07: destaque visual persistente pra OS de garantia/retorno.
            const garantia = !!os.eh_garantia
            return (
              <div key={os.id} onClick={() => nav(`/servicos/${os.id}`)} role="button" tabIndex={0}
                className={`group text-left border rounded-xl p-4 hover:shadow-md transition relative overflow-hidden cursor-pointer ${
                  garantia ? 'os-garantia' : 'bg-white border-gray-200 hover:border-violet-300'}`}
                style={{ borderLeft: `4px solid ${garantia ? '#e11d48' : st.borda}` }}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-bold text-gray-900">#{os.numero}</span>
                    {garantia && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-600 text-white flex items-center gap-1">
                        <Shield className="w-3 h-3" /> GARANTIA
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${st.cor}`}>{st.label}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {urgente && (
                      <span className="px-1.5 py-0.5 rounded text-[9px] bg-red-600 text-white font-bold animate-pulse">URGENTE</span>
                    )}
                    {verArquivadas ? (
                      <button onClick={(e) => restaurarOS(e, os)} title="Restaurar OS"
                        className="p-1 rounded text-gray-400 hover:text-emerald-600 hover:bg-emerald-50">
                        <ArchiveRestore className="w-4 h-4" />
                      </button>
                    ) : (
                      <button onClick={(e) => { e.stopPropagation(); pedirExclusao({ descricao: `a OS #${os.numero}`, confirmLabel: 'Arquivar', onConfirm: () => arquivarOS(e, os) }) }} title="Arquivar (excluir do dia a dia)"
                        className="p-1 rounded text-gray-300 hover:text-amber-600 hover:bg-amber-50">
                        <Archive className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-1">
                  <UserIcon className="w-3.5 h-3.5 text-gray-400" />
                  <span className="truncate">{os.cliente_nome || `Cliente ${os.cliente_id}`}</span>
                </div>
                {os.equipamento_nome && (
                  <div className="flex items-center gap-1.5 text-xs text-violet-600 mb-1">
                    <Bike className="w-3 h-3" />
                    <span className="truncate">{os.equipamento_nome}</span>
                  </div>
                )}
                <p className="text-xs text-gray-500 line-clamp-2 min-h-[2rem]">{os.descricao_problema || 'Sem descrição'}</p>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-3 text-[11px] text-gray-500">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{tempoDesde(os.data_abertura)}</span>
                    {os.prioridade !== 'normal' && <span className={pr.cor}>{pr.label}</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="font-mono font-bold text-gray-900">{formatMoney(Number(os.valor_total))}</span>
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-violet-500 transition" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function KpiCard({ icon: Icon, label, valor, cor }: any) {
  const cores: any = {
    violet:  { card: 'bg-violet-50 text-violet-700 border-violet-100',   ic: 'bg-violet-600' },
    indigo:  { card: 'bg-indigo-50 text-indigo-700 border-indigo-100',   ic: 'bg-indigo-600' },
    orange:  { card: 'bg-orange-50 text-orange-700 border-orange-100',   ic: 'bg-orange-500' },
    emerald: { card: 'bg-emerald-50 text-emerald-700 border-emerald-100', ic: 'bg-emerald-600' },
  }
  const c = cores[cor] || cores.violet
  return (
    <div className={`border rounded-xl p-3 flex items-center gap-3 ${c.card}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white ${c.ic}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase font-semibold opacity-70 truncate">{label}</div>
        <div className="text-lg font-bold truncate">{valor}</div>
      </div>
    </div>
  )
}
